import React, {
  ComponentProps,
  FC,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { ProjectId } from 'api/models';
import { ActionPanel } from 'components/molecules/action-panel';
import { useAssigneeState } from 'components/organisms/tasks/bulk-editor/assignee/use-assignee-state';
import {
  AssigneeField,
  AssigneeValue,
} from 'components/organisms/tasks/fields';
import { AssigneePicker, BaseProps } from './assignee-picker';
import { useAssignTeamsToProject } from './use-assign-teams-to-project';

export type BulkAssigneePickerProps = BaseProps & {
  assignee: AssigneeField;
  projectIds: ProjectId[];
  isPrivate: boolean;
  saveTitle: ReactNode;
  title: ReactNode;
  allowed: ComponentProps<typeof PopoverAction>['allowed'];
  onAssigneeChange: (value: AssigneeValue[]) => void;
  addTeamsToProject?: (projectId: number, teamIds: number[]) => void;
};

const { PopoverAction } = ActionPanel;

export const BulkAssigneePicker: FC<BulkAssigneePickerProps> = (props) => {
  const {
    saveTitle,
    assignee,
    isPrivate,
    projectIds,
    onAssigneeChange,
    title,
    allowed,
  } = props;

  const { addTeamsToProject } = useAssignTeamsToProject();

  const { clear, options, onValueChange, primary, innerRef } = useAssigneeState(
    {
      saveTitle,
      assignee,
      projectIds,
      isPrivate,
      onChange: onAssigneeChange,
    },
  );

  const addTeamsHandler = useCallback(
    (selectedOptions) => {
      projectIds.forEach((projectId) => {
        addTeamsToProject(projectId, selectedOptions);
      });
    },
    [addTeamsToProject, projectIds],
  );

  const popoverRef = useRef(null);
  const isSingleProjectSelected = projectIds.length === 1;

  return (
    <PopoverAction
      ref={innerRef}
      title={title}
      allowed={allowed}
      onShow={clear}
    >
      <div ref={popoverRef}>
        <AssigneePicker
          title={title}
          hideTrigger
          hidePlaceholderCreate
          hidePlaceholderSection
          preventSubmitOnDismiss
          projectId={isSingleProjectSelected ? projectIds[0] : undefined}
          value={[]}
          popoverProps={{
            placement: 'left',
          }}
          containerRef={popoverRef}
          onDone={(selectedOptions) => {
            addTeamsHandler(selectedOptions);
            primary.onClick();
          }}
          selectProps={{
            options,
            onChange: onValueChange,
          }}
          isPrivate={isPrivate}
        />
      </div>
    </PopoverAction>
  );
};
